export const FORM_OFFICE_API_VALUES = ["MALTA", "UKRAINE", "POLAND"] as const;

export type FormOfficeApi = (typeof FORM_OFFICE_API_VALUES)[number];

/** Maps task/applicant office to POST /applicant-accounting/create `form_office` enum. */
export const normalizeFormOfficeForApi = (value?: string | null): FormOfficeApi | "" => {
  const upper = String(value ?? "").trim().toUpperCase();
  if (!upper || upper === "ALL") return "";
  return (FORM_OFFICE_API_VALUES as readonly string[]).includes(upper) ? (upper as FormOfficeApi) : "";
};

export const normalizeFormOfficesForApi = (office?: string | string[] | null): FormOfficeApi[] => {
  if (office == null) return [];
  const values = Array.isArray(office) ? office : [office];
  return Array.from(
    new Set(values.map((value) => normalizeFormOfficeForApi(value)).filter((value): value is FormOfficeApi => value !== "")),
  );
};

/**
 * Query fragment: send `office` for one or more real offices.
 * `ALL` / empty is omitted. Arrays become repeated query params (e.g. office=MALTA&office=POLAND).
 * Kept for Applications / Tasks endpoints that still accept a single string.
 */
export const withOfficeQuery = (
  office?: string | string[] | null,
): { office: FormOfficeApi | FormOfficeApi[] } | Record<string, never> => {
  const normalized = normalizeFormOfficesForApi(office);
  if (normalized.length === 0) return {};
  return { office: normalized.length === 1 ? normalized[0] : normalized };
};

/**
 * Office Accounting list/filters apply: OpenAPI `office: array<string> | null`.
 * Always sends an array when set; omits the param when empty / ALL (null).
 * Serialized as repeated keys: `office=POLAND&office=UKRAINE`.
 */
export const withOfficeArrayQuery = (
  office?: string | string[] | null,
): { office: FormOfficeApi[] } | Record<string, never> => {
  const normalized = normalizeFormOfficesForApi(office);
  if (normalized.length === 0) return {};
  return { office: normalized };
};

/**
 * Axios paramsSerializer for Office Accounting: arrays → `key=a&key=b` (no `key[]`).
 * Empty / null values are omitted (All → no `office`).
 */
export const serializeOfficeAccountingQueryParams = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item == null || item === "") return;
        search.append(key, String(item));
      });
      return;
    }

    search.append(key, String(value));
  });

  return search.toString();
};
